import React, { useMemo } from 'react'
import {
  IClinicNumber,
  ITwilioAvailableNumber,
  ITwilioPurchaseNumberPricing,
} from '@dataTypes/clinic.type'
import { useAppSelector } from '@store/hooks'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import {
  Alert,
  Badge,
  DataTable,
  SelectionBoxGroup,
  type DataTableColumn,
} from '@enamelai/design-system'

type NumberTableRow = { id: string }

interface SelectNumberProps {
  selectedNumber: ITwilioAvailableNumber | null
  setSelectedNumber: (number: ITwilioAvailableNumber | null) => void
  numberData: IClinicNumber
  handleChange: (field: string, value: string | boolean | null) => void
  selectedType: 'local' | 'toll-free' | null
  setSelectedType: (type: 'local' | 'toll-free' | null) => void
  pricing: ITwilioPurchaseNumberPricing | null
  isPricingLoading: boolean
  availableNumbers: ITwilioAvailableNumber[]
  isNumbersLoading: boolean
  fetchError: string | null
}

const SelectNumber: React.FC<SelectNumberProps> = ({
  numberData,
  handleChange,
  selectedType,
  setSelectedType,
  pricing,
  isPricingLoading,
  availableNumbers,
  isNumbersLoading,
  fetchError,
  setSelectedNumber,
}) => {
  const { loading: commsLoading } = useAppSelector(
    (state) => state.settingsCommunication,
  )
  const { loading: clinicLoading } = useAppSelector((state) => state.clinic)
  const typeDisabled = commsLoading || clinicLoading || isPricingLoading

  const renderMonthlyPrice = (type: 'local' | 'toll-free'): string => {
    if (isPricingLoading) return 'Loading...'
    const amountValue =
      type === 'local'
        ? pricing?.local_monthly_price
        : pricing?.toll_free_monthly_price
    const amount = Number.parseFloat(String(amountValue))
    if (Number.isNaN(amount)) return 'N/A'
    const formattedAmount = amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
    return `+$${formattedAmount}/mo`
  }

  const featureLabels = (item: ITwilioAvailableNumber): string[] => {
    const features: string[] = []
    if (item.capabilities?.voice) features.push('Call')
    if (item.capabilities?.MMS) features.push('MMS')
    if (item.capabilities?.SMS) features.push('SMS')
    return features
  }

  const getContactMethods = (item: ITwilioAvailableNumber): string => {
    const methods: string[] = []
    if (item.capabilities?.voice) methods.push('call')
    if (item.capabilities?.MMS) methods.push('mms')
    if (item.capabilities?.SMS) methods.push('sms')
    return JSON.stringify(methods)
  }

  const numbersById = useMemo(() => {
    const map = new Map<string, ITwilioAvailableNumber>()
    availableNumbers.forEach((item) => {
      map.set(normalizePhoneNumber(item.phoneNumber), item)
    })
    return map
  }, [availableNumbers])

  const columns: DataTableColumn<NumberTableRow>[] = [
    {
      id: 'number',
      label: 'Number',
      renderCell: (row) => {
        const item = numbersById.get(row.id)
        return item ? formatPhoneNumber(item.phoneNumber, false) : row.id
      },
    },
    {
      id: 'features',
      label: 'Supported features',
      renderCell: (row) => {
        const item = numbersById.get(row.id)
        const features = item ? featureLabels(item) : []
        if (features.length === 0) return '—'
        return (
          <div className="scp-pill-container">
            {features.map((feature) => (
              <Badge
                key={feature}
                mode="tinted"
                color={
                  feature === 'SMS'
                    ? 'cyan'
                    : feature === 'MMS'
                      ? 'pale-green'
                      : 'blue'
                }
                label={feature}
              />
            ))}
          </div>
        )
      },
    },
  ]

  const selectNumber = (phoneNumber: string): void => {
    const item = numbersById.get(phoneNumber)
    if (!item) {
      handleChange('phone_number', '')
      handleChange('contact_methods', null)
      setSelectedNumber(null)
      return
    }
    handleChange('phone_number', phoneNumber)
    handleChange('contact_methods', getContactMethods(item))
    setSelectedNumber(item)
  }

  return (
    <div className="scp-pm-card">
      <SelectionBoxGroup
        label="Number type"
        value={selectedType ?? ''}
        disabled={typeDisabled}
        onChange={(value) => {
          setSelectedType(value as 'local' | 'toll-free')
        }}
        options={[
          {
            value: 'local',
            title: 'Local numbers',
            description: `Area specific numbers · ${renderMonthlyPrice('local')}`,
          },
          {
            value: 'toll-free',
            title: 'Toll-free numbers',
            description: `Nationwide non-geographic numbers · ${renderMonthlyPrice('toll-free')}`,
          },
        ]}
      />
      {selectedType && fetchError ? (
        <Alert
          variant="critical"
          layout="board"
          title="Could not load numbers"
          description={fetchError}
          showActions={false}
          showMenu={false}
        />
      ) : null}
      {selectedType && !fetchError ? (
        <DataTable<NumberTableRow>
          className="purchase-number-list"
          columns={columns}
          rows={availableNumbers.map((item) => ({
            id: normalizePhoneNumber(item.phoneNumber),
          }))}
          selectable
          showActions={false}
          selectedRowIds={
            numberData.phone_number ? [numberData.phone_number] : []
          }
          onSelectedRowIdsChange={(ids) => {
            const nextId = ids[ids.length - 1] ?? ''
            selectNumber(nextId)
          }}
          state={
            isNumbersLoading
              ? 'loading'
              : availableNumbers.length === 0
                ? 'empty'
                : 'default'
          }
          emptyState={{
            title: 'No numbers available',
            description:
              'No numbers available for the selected clinic address.',
          }}
        />
      ) : null}
    </div>
  )
}

export default SelectNumber
